import posthog from 'posthog-js';
import { User } from '@/types/user.types';

export interface AnalyticsConfig {
  enableAnalytics: boolean;
  posthogKey: string;
  posthogHost?: string;
  /** When false, disables session recording. Default true. */
  sessionReplayEnabled?: boolean;
}

export type AnalyticsEventProperties = Record<string, unknown>;

type PosthogClient = Pick<typeof posthog, 'capture' | 'identify' | 'reset' | 'people'>;

let posthogClient: PosthogClient | null = null;

export const isAnalyticsEnabled = ({ enableAnalytics, posthogKey }: AnalyticsConfig) =>
  Boolean(enableAnalytics && posthogKey && posthogKey.trim().length > 0);

export const initPosthog = (config: AnalyticsConfig): PosthogClient | null => {
  if (!isAnalyticsEnabled(config)) {
    if (posthogClient) {
      posthogClient.reset();
      posthogClient = null;
    }
    return null;
  }

  if (!posthogClient) {
    posthog.init(config.posthogKey, {
      api_host: config.posthogHost || 'https://us.i.posthog.com',
      capture_pageview: false,
      // Fire $pageleave on unload/external navigation so PostHog can compute
      // exit pages. SPA route changes are covered by trackPageView + the
      // engagement module (page_engagement events carry durations).
      capture_pageleave: true,
      persistence: 'localStorage',
      // Session replay can be switched off via VITE_POSTHOG_SESSION_REPLAY.
      disable_session_recording: config.sessionReplayEnabled === false,
      session_recording: {
        // Mask every input value in session replays (passwords are always
        // masked by the SDK; this extends it to search boxes, forms, etc).
        maskAllInputs: true,
        // Elements with these classes are never recorded / text-masked.
        blockClass: 'ph-no-capture',
        maskTextClass: 'ph-mask',
      },
    });
    posthogClient = posthog;
  }

  return posthogClient;
};

export const identifyUser = (
  user: Partial<User> & { id?: string; email?: string; role?: string },
  customProperties: AnalyticsEventProperties = {}
) => {
  const client = posthogClient ?? initPosthog({
    enableAnalytics: Boolean(import.meta.env.VITE_ENABLE_ANALYTICS === 'true'),
    posthogKey: String(import.meta.env.VITE_POSTHOG_KEY || ''),
    posthogHost: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
  });

  if (!client || !user?.id) {
    return;
  }

  // VITE_POSTHOG_EXCLUDE_STAFF: admins are excluded from analytics entirely —
  // opt out stops all capture for this browser and clears any queued identity.
  const excludeStaff = import.meta.env.VITE_POSTHOG_EXCLUDE_STAFF === 'true';
  if (excludeStaff && user.role === 'admin') {
    posthog.opt_out_capturing();
    resetPosthog();
    return;
  }

  const profileProperties = {
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
    role: user.role,
    ...customProperties,
  };

  client.identify(user.id, profileProperties);

  if (typeof client.people?.set === 'function') {
    client.people.set(profileProperties);
  }
};

export const trackEvent = (
  eventName: string,
  properties: AnalyticsEventProperties = {},
  client: PosthogClient | null = posthogClient
) => {
  if (!client) {
    return;
  }

  client.capture(eventName, properties);
};

export const trackPageView = (
  path: string,
  properties: AnalyticsEventProperties = {},
  client: PosthogClient | null = posthogClient
) => {
  if (!client) {
    return;
  }

  const url = typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;

  client.capture('$pageview', {
    $current_url: url,
    path,
    ...properties,
  });
};

export const resetPosthog = () => {
  if (posthogClient) {
    posthogClient.reset();
  }
  posthogClient = null;
};
